import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, inrFull, COLORS, statusColor } from "@/lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, BarChart, Bar, Cell } from "recharts";

export default function YieldScreen() {
  const { state } = usePlant();
  const [agg, setAgg] = useState([]);
  const [heats, setHeats] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, h] = await Promise.all([
          api.get("/plant/heats/aggregate?days=30"),
          api.get("/plant/heats?limit=100"),
        ]);
        if (alive) {
          setAgg(a.data.days || []);
          setHeats(h.data.heats || []);
        }
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;
  const m = state.metrics || {};
  const yieldPct = m.ce_22 || state.shift?.yield_pct || 72;
  const yieldColor = yieldPct > 75 ? COLORS.green : yieldPct > 70 ? COLORS.yellow : COLORS.red;

  // Rejection reason aggregation
  const rejCounts = {};
  heats.forEach((h) => {
    if (h.rejection_reason && h.rejection_kg > 0) {
      rejCounts[h.rejection_reason] = (rejCounts[h.rejection_reason] || 0) + h.rejection_kg;
    }
  });
  const rejData = Object.entries(rejCounts).map(([k, v]) => ({ name: k, value: Math.round(v) })).sort((a, b) => b.value - a.value);

  const target = state.plant_params?.target_yield_pct || 76;
  const monthlyImprovement = (target - yieldPct) * state.month.tonnes_melted * 6.5;

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Yield Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Yield = Energy Per Saleable Tonne</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="yield-gauge">
          <div className="label-small">Yield · current shift</div>
          <div className="metric-massive mt-2" style={{ color: yieldColor }}>{num(yieldPct, 1)}<span className="text-xl ml-1">%</span></div>
          <div className="text-xs text-[#8F93A2]">Target {target}% · Warning &lt; 70%</div>
        </div>

        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="yield-penalty">
          <div className="label-small">Yield Energy Penalty</div>
          <div className="metric-large text-[#FF3333] mt-2">{inrFull(m.ce_23)}</div>
          <div className="text-xs text-[#8F93A2]">/month at current yield vs target</div>
          <div className="mt-3 p-3 bg-[#14151C] border-l-2 border-[#00E5FF] text-sm">
            Improving yield to {target}% would save <span className="font-mono text-[#00FF66]">{inrFull(m.ce_23)}/month</span> — zero capital investment.
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="yield-returns">
          <div className="label-small">Returns vs Permanent Loss</div>
          <div className="space-y-2 mt-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#8F93A2]">Returns remelted</span>
              <span className="font-mono">{Math.round(m.ce_24)} kWh</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#8F93A2]">Permanent loss energy</span>
              <span className="font-mono text-[#FF3333]">{Math.round(m.ce_25)} kWh</span>
            </div>
            <div className="border-t border-[#1F212A] pt-2">
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">cost of wasted melt</div>
              <div className="metric-mid text-[#FF3333]">{inrFull(m.ce_25 * state.plant_params.tariff_per_kwh)}</div>
            </div>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Yield Trend · last 30 days</span></div>
          <div className="p-3 h-[240px]" data-testid="yield-trend">
            <ResponsiveContainer>
              <LineChart data={agg}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke={COLORS.textDim} fontSize={10} />
                <YAxis domain={[55, 85]} stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={target} stroke={COLORS.green} strokeDasharray="3 3" label={{ value: "Target", fill: COLORS.green, fontSize: 10 }} />
                <ReferenceLine y={70} stroke={COLORS.red} strokeDasharray="3 3" label={{ value: "Warning", fill: COLORS.red, fontSize: 10 }} />
                <Line type="monotone" dataKey="yield_pct" stroke={COLORS.blue} strokeWidth={2} dot={false}  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Rejection by Cause (by weight)</span></div>
          <div className="p-3 h-[220px]" data-testid="rejection-chart">
            <ResponsiveContainer>
              <BarChart data={rejData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <Bar dataKey="value" fill={COLORS.red}  isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel p-5">
          <div className="label-small">Yield vs Operational Variables — Model 5</div>
          <div className="text-sm text-[#8F93A2] mt-2 leading-relaxed">
            Model 5 (Yield Correlation) is in cold-start mode — gathering 30 days of paired data
            to identify which operational variables most strongly predict yield deterioration.
            Correlation scatter plots activate once baseline is established.
          </div>
          <div className="mt-4 space-y-2">
            <CorrRow label="Tap temperature" status="learning" />
            <CorrRow label="Holding time (CE-12)" status="learning" />
            <CorrRow label="Charge quality (CE-26)" status="learning" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CorrRow({ label, status }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[#14151C] text-sm">
      <span>{label}</span>
      <span className="badge-status blue">{status}</span>
    </div>
  );
}
